import React from "react";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import NoDataIllustration from "Components/DesignSystem/EmptyState/NoDataIllustration";

export type TNoDataComponent = {
  message?: string;
  title?: string;
  'data-testid'?: string;
}

/**
 * The shared "no data" moment, delegating to the DS `EmptyState` (2026-09-08).
 *
 * It used to hand-roll the layout: `<img alt="logo">` of the fixed-colour
 * `place_holder_no_data.svg`, a title on the `#172B4D` navy literal and a message on
 * `#6D788C` — which is exactly the combination `NoDataIllustration`'s docblock calls
 * out. Under `data-theme="dark"` the title vanished into the ground (user screenshot,
 * Mapping). Delegating gets the tokenized artwork (brand navy in light, brand cyan in
 * dark, surface-filled cutouts) and `ink-secondary`/`ink-tertiary` text everywhere it
 * renders: Mapping's Base/Category tables, Recategorization's three attribute tables,
 * ABTesting's ResultAnalytics, and the legacy `Table` injections in
 * ComputedSubstitutionView and RuleView.
 *
 * The outer centering shell stays — consumers drop this into arbitrary-height
 * containers and rely on it. `data-testid` is forwarded, never defaulted: the three
 * Recategorization tables can render this simultaneously, and a baked-in id would
 * duplicate.
 */
const NoDataComponent = ({ message, title, 'data-testid': dataTestId }: TNoDataComponent) => {
  return (
    <div className="tw-flex tw-justify-center tw-items-center tw-h-[100%]">
      <EmptyState
        title={title ?? "No Data Found"}
        description={message}
        illustration={<NoDataIllustration />}
        data-testid={dataTestId}
      />
    </div>
  );
};

export default NoDataComponent;
